/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { BEM } from "@adapptio/lib-components";
import { useDndDraggableContext } from "../DndDraggable/DndDraggableContext";
import { overrideForwardRef } from "../OverridableComponent";
import * as attributes from "../Dnd/dndAttributes";
import { preventHtml5Dnd } from "../Dnd/utils";

export interface IDndDragHandleProps {
	children?: React.ReactNode;
	className?: string;
}

export const DndDragHandle = overrideForwardRef<IDndDragHandleProps, "div">(
	({ Component, children, className, ...props }, ref) => {
		const baseClass = BEM("edt-dnd-drag-handle");

		const draggableCtx = useDndDraggableContext();

		const dragHandleProps = useMemo(
			() =>
				draggableCtx && draggableCtx.dndContextId && !draggableCtx.disabled
					? {
							tabIndex: 0,
							role: "button",
							[attributes.dragHandle.draggableId]: draggableCtx.draggableId,
							[attributes.dragHandle.contextId]: draggableCtx.dndContextId,
							draggable: false,
							onDragStart: preventHtml5Dnd
					  }
					: null,
			[ draggableCtx ]
		);

		if (className) {
			baseClass.add(className);
		}

		if (!draggableCtx.dndContextId) {
			return null;
		}

		return (
			<Component {...props} {...dragHandleProps} className={baseClass.toClassName()} ref={ref}>
				{children}
			</Component>
		);
	}
);

DndDragHandle.defaultProps = {
	Component: "div"
};
